/**
 * S19 — Measure · Benchmarking & Peer Comparison (`/measure/benchmark`).
 *
 * Wave 31 — Phase 3b. Migrated off `useTenantData().benchmark` onto the
 * new `/api/metrics/benchmarking` REST endpoint via `useEntity()`.
 */
import React from "react";
import { LineChart, Award, ShieldCheck } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTenantData } from "@/contexts/TenantModeContext";
import { useEntity } from "@/lib/useEntity";
import { SyncedIndicator } from "@/components/data/SyncedIndicator";
import { Flywheel } from "@/components/brand/Flywheel";
import { KpiTile } from "@/pages/dashboard/components/DashboardPrimitives";
import { cn } from "@/lib/utils";

export default function Benchmarking() {
  const { user } = useAuth();
  const { isCustomer } = useTenantData();
  const {
    data,
    loading,
    error,
    lastSyncedAt,
    refetch,
  } = useEntity("/api/metrics/benchmarking");
  const benchmark = data?.benchmark;

  if (loading && !data) return <Skeleton />;
  if (error) return <ErrorView message={error.message} onRetry={refetch} />;
  if (isCustomer || !benchmark) return <Empty userName={user?.name?.split(" ")[0]} />;

  const aboveP75 = benchmark.metrics.filter((m) => quartile(m) === "topQ").length;
  const belowP25 = benchmark.metrics.filter((m) => quartile(m) === "bottomQ").length;

  return (
    <div className="relative px-6 lg:px-8 py-6 anim-fade-up" data-testid="benchmarking">
      <div className="absolute -right-24 -top-12 pointer-events-none">
        <Flywheel size={300} opacity={0.05} />
      </div>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="eyebrow">Measure · Benchmarking & Peer Comparison</p>
          <h1 className="text-display-md text-ink-primary mt-1">Benchmarking</h1>
          <p className="text-ink-secondary mt-1 max-w-2xl">
            Anonymised peer comparison — see where you stand against the
            <span className="text-ink-primary font-medium">
              {" "}
              {benchmark.peerGroup}
            </span>
            . Last refreshed {benchmark.refreshedAt}.
          </p>
        </div>
        <SyncedIndicator
          lastSyncedAt={lastSyncedAt}
          loading={loading}
          onRefetch={refetch}
          testId="benchmarking-synced"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
        <KpiTile label="Metrics tracked" value={benchmark.metrics.length.toString()} delta={benchmark.peerGroup} deltaTone="neutral" hint="across cost, quality, compliance" icon={LineChart} tone="tralenex" testId="bench-kpi-tracked" />
        <KpiTile label="Top-quartile" value={aboveP75.toString()} delta="ahead of p75 peers" deltaTone="up" hint="leadership position" icon={Award} tone="gold" testId="bench-kpi-top" />
        <KpiTile label="Below-median" value={belowP25.toString()} delta={belowP25 === 0 ? "no laggard metrics" : "investigate"} deltaTone={belowP25 === 0 ? "up" : "down"} hint="opportunities to lift" icon={ShieldCheck} tone="risk" testId="bench-kpi-laggard" />
      </div>

      <div className="mt-6 surface-card shadow-e1 overflow-hidden" data-testid="bench-table">
        <div className="px-4 py-3 border-b border-border-subtle">
          <p className="eyebrow">Peer percentile band</p>
          <p className="text-sm text-ink-secondary mt-0.5">
            Solid bar = peer p25 → p90 range · colored pill = your position · arrows show
            whether higher is better.
          </p>
        </div>
        <ul className="divide-y divide-border-subtle">
          {benchmark.metrics.map((m) => (
            <Row key={m.metric} m={m} />
          ))}
        </ul>
      </div>
    </div>
  );
}

function quartile(m) {
  if (m.direction === "down") {
    if (m.ours <= m.p90) return "topQ";
    if (m.ours >= m.p25) return "bottomQ";
  } else {
    if (m.ours >= m.p75) return "topQ";
    if (m.ours <= m.p25) return "bottomQ";
  }
  return "midQ";
}

function Row({ m }) {
  const min = Math.min(m.p25, m.p50, m.p75, m.p90, m.ours);
  const max = Math.max(m.p25, m.p50, m.p75, m.p90, m.ours);
  const span = max - min || 1;
  const pct = (v) => ((v - min) / span) * 100;
  const q = quartile(m);
  const tone = q === "topQ" ? "bg-risk-low" : q === "bottomQ" ? "bg-risk-high" : "bg-gold-600";
  return (
    <li className="px-4 py-3" data-testid={`bench-row-${m.metric.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-baseline justify-between gap-4">
        <div>
          <p className="text-ink-primary text-sm font-medium">{m.metric}</p>
          <p className="text-meta text-ink-secondary">
            Higher is {m.direction === "down" ? "worse" : m.direction === "up" ? "better" : "context-dependent"}
          </p>
        </div>
        <div className="text-right shrink-0">
          <p className={cn("text-h2 tabular-nums font-semibold", q === "topQ" ? "text-risk-low" : q === "bottomQ" ? "text-risk-high" : "text-ink-primary")}>
            {fmt(m.ours, m.unit)}
          </p>
          <p className="text-meta text-ink-secondary">vs p50 {fmt(m.p50, m.unit)}</p>
        </div>
      </div>
      <div className="mt-3 relative h-2 rounded bg-inset/60">
        <div className="absolute inset-y-0 rounded bg-tralenex-500/30" style={{ left: `${pct(m.p25)}%`, width: `${pct(m.p90) - pct(m.p25)}%` }} />
        <div className="absolute -top-1 w-0.5 h-4 bg-tralenex-700" style={{ left: `${pct(m.p50)}%` }} title={`p50 · ${fmt(m.p50, m.unit)}`} />
        <div className={cn("absolute -top-1.5 w-3 h-5 rounded-sm", tone)} style={{ left: `calc(${pct(m.ours)}% - 6px)` }} title={`Our value · ${fmt(m.ours, m.unit)}`} />
      </div>
      <div className="mt-1 flex justify-between text-meta font-mono text-ink-secondary tabular-nums">
        <span>p25 {fmt(m.p25, m.unit)}</span>
        <span>p75 {fmt(m.p75, m.unit)}</span>
        <span>p90 {fmt(m.p90, m.unit)}</span>
      </div>
    </li>
  );
}

function fmt(v, unit) {
  if (unit === "%") return `${v}%`;
  if (unit === "x") return `${v}x`;
  if (unit === "hrs") return `${v}h`;
  return `${v}`;
}

function Skeleton() {
  return (
    <div className="px-6 lg:px-8 py-6 anim-fade-up" data-testid="benchmarking-loading">
      <div className="h-7 w-72 rounded-md bg-inset/60 animate-pulse" />
      <div className="mt-2 h-4 w-96 rounded-md bg-inset/40 animate-pulse" />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-24 rounded-md surface-card animate-pulse bg-inset/30" />
        ))}
      </div>
      <div className="mt-6 h-[400px] rounded-md surface-card animate-pulse bg-inset/20" />
    </div>
  );
}

function ErrorView({ message, onRetry }) {
  return (
    <div className="px-6 lg:px-8 py-6" data-testid="benchmarking-error">
      <div className="surface-card shadow-e1 p-5 border-l-[3px] border-l-risk-high">
        <p className="eyebrow text-risk-high">Measure · Benchmarking</p>
        <h1 className="text-h2 text-ink-primary mt-1">Couldn&apos;t load benchmark data</h1>
        <p className="mt-2 text-meta text-ink-secondary">{message || "Network error — please retry."}</p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-4 inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-tralenex-600 text-white text-sm font-semibold hover:bg-tralenex-700"
          data-testid="benchmarking-retry"
        >
          Retry
        </button>
      </div>
    </div>
  );
}

function Empty({ userName }) {
  return (
    <div className="relative min-h-[calc(100vh-112px)] px-6 lg:px-10 py-10 overflow-hidden" data-testid="bench-customer-empty">
      <div className="absolute -right-40 -bottom-40 pointer-events-none">
        <Flywheel size={620} opacity={0.04} />
      </div>
      <div className="relative max-w-2xl">
        <p className="eyebrow">Measure · Benchmarking</p>
        <h1 className="text-display-lg text-ink-primary mt-1">
          {userName ? `Welcome, ${userName}.` : "Welcome."}
        </h1>
        <p className="text-ink-secondary mt-3 text-[15px]">
          Benchmarking is opt-in and anonymised. Once you join the peer group,
          your relative position lights up here.
        </p>
      </div>
    </div>
  );
}
